// ─────────────────────────────────────────────────────────────────────────────
// useEngagementTemplates
//
// Fetches engagement templates for the practice (filtered by category, e.g.
// 'email' or 'whatsapp') and optionally surfaces the templates most relevant
// to the linked lead's current status first.
//
// Status-relevance precedence (B3 fix):
//   1. If ANY template in the response defines the `applies_to_statuses` field
//      (even as an empty array), we treat that as the BE contract being live
//      and use BE filtering only:
//         - empty []                → template applies to ALL statuses
//                                     (shown in the relevant group when a
//                                     statusFilter is set)
//         - non-empty [STATUS, ...] → must include current statusFilter
//   2. Otherwise (legacy server, no template defines the field), we fall back
//      to the STATUS_CATEGORY_HINTS heuristic and log a warning so the
//      legacy state is observable.
//
// Returns:
//   templates       — sorted/grouped list (relevant first when filtering)
//   relevantIds     — Set of ids considered status-relevant (subset of templates)
//   isFiltered      — true when statusFilter was applied AND at least one match
//   fallbackUsed    — true when statusFilter was applied but produced 0 matches
//   loading         — fetch in flight
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState, useMemo } from 'react';
import { callApi } from 'src/utils/apiCall';

// Heuristic: map a lead status → the engagement-template categories most
// likely to be useful for that stage. Order matters (highest priority first).
// Categories come from `ENGAGEMENT_TEMPLATE_CATEGORIES` in
// src/constants/EmailMessageComposeConst.js.
//
// LEGACY-ONLY: used only when the BE response has no template defining the
// `applies_to_statuses` field (server pre-32362b52). Once every server is on
// the new contract this block can be deleted.
const STATUS_CATEGORY_HINTS = {
    NEW:                            ['FOLLOW_UP', 'COMMUNICATION', 'MANUAL', 'REMINDER'],
    CONSULTATION_BOOKED:            ['REMINDER', 'COMMUNICATION', 'TRANSACTIONAL', 'MANUAL'],
    CONSULTATION_ATTENDED:          ['FOLLOW_UP', 'COMMUNICATION', 'PROMOTION', 'MANUAL'],
    FAILED_TO_ATTEND_CONSULTATION:  ['FOLLOW_UP', 'REMINDER', 'COMMUNICATION', 'MANUAL'],
    TREATMENT_STARTED:              ['NOTIFICATION', 'COMMUNICATION', 'TRANSACTIONAL', 'MANUAL'],
    CLOSED:                         ['MARKETING', 'PROMOTION', 'NEWSLETTER', 'MANUAL'],
    ARCHIVED:                       ['MARKETING', 'PROMOTION', 'NEWSLETTER', 'MANUAL'],
};

// Pretty label for the in-menu "Filtered by ..." hint.
export const STATUS_LABELS = {
    NEW:                            'NEW',
    CONSULTATION_BOOKED:            'Consultation Booked',
    CONSULTATION_ATTENDED:          'Consultation Attended',
    FAILED_TO_ATTEND_CONSULTATION:  'Failed to Attend',
    TREATMENT_STARTED:              'Treatment Started',
    CLOSED:                         'Closed',
    ARCHIVED:                       'Archived',
};

export default function useEngagementTemplates({
    practiceId,
    category,        // 'email' | 'whatsapp' | 'review_request' | 'consent' | 'treatment_plan'
    statusFilter,    // optional lead status (e.g. 'NEW') — when present, sort relevant templates first
    enabled = true,
    limit = 20,
}) {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);

    // B2 fix: each effect run owns a `cancelled` flag. If practiceId / category
    // change mid-flight (or the consumer unmounts) we drop the response on the
    // floor instead of overwriting the next request's results. The previous
    // ref-based guard short-circuited every run after the first, leaking
    // template data across leads/practices.
    useEffect(() => {
        if (!enabled || !practiceId || !category) return undefined;
        let cancelled = false;
        setLoading(true);
        callApi(
            `/api/v1/practices/${practiceId}/engagement-templates/?category=${category}&limit=${limit}`,
            'GET'
        )
            .then((res) => {
                if (cancelled) return;
                if (res?.status !== 200) return;
                const list = res?.data?.results;
                let arr = [];
                if (Array.isArray(list)) arr = list;
                else if (Array.isArray(res?.data)) arr = res.data;
                setTemplates(arr);
            })
            .catch((err) => {
                if (!cancelled) {
                    // eslint-disable-next-line no-console
                    console.error('[useEngagementTemplates] fetch failed', err);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [enabled, practiceId, category, limit]);

    const { sorted, relevantIds, isFiltered, fallbackUsed } = useMemo(() => {
        const all = Array.isArray(templates) ? templates : [];
        if (!statusFilter) {
            return { sorted: all, relevantIds: new Set(), isFiltered: false, fallbackUsed: false };
        }

        // B3 fix: detect whether the BE supports `applies_to_statuses`. Presence
        // of the field on ANY template in the response is the contract — empty
        // arrays are valid data ("applies to all statuses"), not "missing".
        const beSupportsField = all.some((t) => Array.isArray(t?.applies_to_statuses));

        let isRelevant;
        if (beSupportsField) {
            isRelevant = (t) => {
                const list = t?.applies_to_statuses;
                if (!Array.isArray(list)) return false; // mixed response — treat undefined as not-relevant
                if (list.length === 0) return true;     // empty = applies to all statuses
                return list.includes(statusFilter);
            };
        } else {
            // Legacy server — no template defines applies_to_statuses. Fall back
            // to the category heuristic and log once per render so the state is
            // observable in the field.
            // eslint-disable-next-line no-console
            console.warn(
                '[useEngagementTemplates] applies_to_statuses missing on every template — using legacy category heuristic. Update BE to expose the field.'
            );
            const hintCats = STATUS_CATEGORY_HINTS[statusFilter] || [];
            isRelevant = (t) => {
                const cat = (t?.category || '').toUpperCase();
                return hintCats.includes(cat);
            };
        }

        const relevant = all.filter(isRelevant);
        if (relevant.length === 0) {
            // No matches — show full list and let caller advertise fallback.
            return { sorted: all, relevantIds: new Set(), isFiltered: false, fallbackUsed: true };
        }
        const others = all.filter((t) => !isRelevant(t));
        const ids = new Set(relevant.map((t) => t.id));
        return { sorted: [...relevant, ...others], relevantIds: ids, isFiltered: true, fallbackUsed: false };
    }, [templates, statusFilter]);

    return {
        templates: sorted,
        relevantIds,
        isFiltered,
        fallbackUsed,
        loading,
    };
}
